import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { performLogout } from '@/utils/auth.ts';
import { getAuthToken, authApi } from '@/services/api';

interface UseIdleTimeoutOptions {
  timeout?: number; // in milliseconds
  onIdle?: () => void;
  onWarning?: (remainingSeconds: number) => void;
  warningTime?: number; // in milliseconds - show warning before timeout
}

/**
 * Hook to handle automatic logout after idle timeout
 * Tracks user activity and logs out after specified time of inactivity
 */
export function useIdleTimeout({
  timeout = 15 * 60 * 1000, 
  onIdle,
  onWarning,
  warningTime = 60 * 1000, // 1 minute warning before logout
}: UseIdleTimeoutOptions = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningShownRef = useRef<boolean>(false);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    isWarningShownRef.current = false;
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeouts();
    lastActivityRef.current = Date.now();
    isWarningShownRef.current = false;

    // Only set timer if user is authenticated
    if (!getAuthToken()) {
      return;
    }

    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      if (onWarning && getAuthToken()) {
        // Calculate remaining seconds (warningTime in seconds)
        const remainingSeconds = Math.ceil(warningTime / 1000);
        onWarning(remainingSeconds);
        isWarningShownRef.current = true;
      }
    }, timeout - warningTime);

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      if (getAuthToken()) {
        if (onIdle) {
          onIdle();
        } else {
          // Default logout behavior
          performLogout(queryClient, navigate);
        }
      }
    }, timeout);
  }, [timeout, warningTime, onIdle, onWarning, queryClient, navigate, clearTimeouts]);

  // Throttle activity detection to prevent too frequent timer resets
  const activityThrottleRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleActivity = useCallback(() => {
    // Clear any pending throttle
    if (activityThrottleRef.current) {
      clearTimeout(activityThrottleRef.current);
    }
    
    // Throttle: only reset timer max once every 3 seconds
    // This prevents constant resets while still being responsive
    activityThrottleRef.current = setTimeout(() => {
      const now = Date.now();
      // Only reset if there was actual meaningful activity
      if (now - lastActivityRef.current > 1000) {
        resetTimer();
      }
      activityThrottleRef.current = null;
    }, 3000);
  }, [resetTimer]);

  useEffect(() => {
    // Only set up idle timeout if user is authenticated
    if (!getAuthToken()) {
      clearTimeouts();
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current);
      }
      return;
    }

    // Set initial timer
    resetTimer();

    // Only track events that indicate actual user interaction (not mousemove)
    const events = [
      'mousedown',    // Mouse click
      'keydown',      // Keyboard press
      'keypress',     // Keyboard input
      'scroll',       // Scrolling
      'touchstart',   // Touch interaction
      'click',        // Click events
      'focus',        // Input focus
      'input',        // Form input
      'change',       // Form changes
    ];
    
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also track visibility change (tab focus)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && getAuthToken()) {
        // Validate token is still valid when tab becomes visible
        try {
          await authApi.getCurrentUser();
          // Token is valid, reset timer
          resetTimer();
        } catch (error) {
          // Token expired or invalid, logout user
          console.warn('Token validation failed on tab return:', error);
          performLogout(queryClient, navigate);
        }
      } else if (document.visibilityState === 'hidden') {
        // Pause timer when tab is hidden (don't count idle time)
        clearTimeouts();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeouts();
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current);
        activityThrottleRef.current = null;
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleActivity, resetTimer, clearTimeouts]);

  return {
    resetTimer,
    clearTimeouts,
  };
}

